import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { SubscriptionTableHeader, SubscriptionTableHeaderProps } from 'src/views/subscriptions/table/header'
import { SubscriptionTableRow } from 'src/views/subscriptions/table/row'
import { SUBSCRIPTION_SECTIONS } from 'src/constants/subscriptions'
import { FANCY_SCROLLBAR } from 'src/utils/styles'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useScrollTop } from 'src/components/table'
import { rgbaStrOpacity } from 'src/utils/colors'

const SubscriptionGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: solid 1px ${Colors.border};
  border-radius: ${Shapes.borderRadius}px;
  overflow-y: auto;
  ${FANCY_SCROLLBAR};
`
// Make this sticky when scrolling
const SubscriptionTableHeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`
const SubscriptionTableBody = styled.div`
  flex: 1;
`

const BottomScrollShadow = styled.div`
  position: sticky;
  bottom: 0;
  background: linear-gradient(0deg, ${rgbaStrOpacity(Colors.neutral950, 0.05)}, transparent);
  width: 100%;
  height: 16px;
`

export const SubscriptionsGrid = (props: SubscriptionTableHeaderProps) => {
  const { onUpgrade, onDowngrade } = props
  const tableRef = useRef<HTMLDivElement>(null)
  const [{ scrollEnd, scrollHeight }, setScrollConfig] = useState<{
    scrollEnd: boolean
    scrollHeight: number
  }>({
    scrollEnd: false,
    scrollHeight: 0,
  })
  const { scrollTop, onScroll } = useScrollTop(false)

  const onScrollTopChange = useCallback(() => {
    if (!tableRef.current) {
      return
    }

    const top = scrollTop || tableRef.current.scrollTop
    const offsetHeight = tableRef.current.offsetHeight
    const scrollHeight = tableRef.current.scrollHeight

    const scrollEnd = top + offsetHeight >= scrollHeight

    setScrollConfig({
      scrollEnd,
      scrollHeight,
    })
  }, [scrollTop, setScrollConfig])

  useEffect(() => {
    onScrollTopChange()
  }, [onScrollTopChange])

  const renderTableFooterShadow = () => {
    const tableHeight = 400

    return (
      <>
        {tableHeight && scrollHeight && scrollHeight > tableHeight && (
          <BottomScrollShadow
            style={{
              opacity: scrollEnd ? 0 : 1,
            }}
          />
        )}
      </>
    )
  }

  return (
    <SubscriptionGridWrapper
      ref={tableRef}
      onScroll={onScroll as (event: React.UIEvent<HTMLDivElement, UIEvent>) => void}>
      <SubscriptionTableHeaderWrapper
        style={{
          boxShadow: scrollTop > 1 ? `0px 16px 10px ${rgbaStrOpacity(Colors.neutral950, 0.05)}` : 'none',
          borderBottom: scrollTop > 1 ? `1px solid ${Colors.border}` : 'none',
        }}>
        <SubscriptionTableHeader onUpgrade={onUpgrade} onDowngrade={onDowngrade} />
      </SubscriptionTableHeaderWrapper>
      <SubscriptionTableBody>
        {SUBSCRIPTION_SECTIONS.map((section, index) => (
          <SubscriptionTableRow key={section.title} section={section} />
        ))}
        {renderTableFooterShadow()}
      </SubscriptionTableBody>
    </SubscriptionGridWrapper>
  )
}
